/**
 * Meter Service
 * Handles meter management, readings, and consumption tracking
 */

import { api_service } from './api';
import {
  Meter,
  MeterReading,
  MeterCreateData,
  MeterUpdateData,
  ReadingCreateData,
  MeterFilters,
  MeterReadingStats,
  CreateMeterReadingPayload, UnbilledReadingForInvoicing
} from '@/types/meter_types';

// ============ TYPE DEFINITIONS ============

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface ReadingFilters {
  meter_id?: number;
  client_id?: number;
  date_from?: string;
  date_to?: string;
  has_invoice?: boolean;
}

export interface MeterStatistics {
  total_meters: number;
  active_meters: number;
  inactive_meters: number;
  faulty_meters: number;
  readings_this_month: number;
  pending_readings: number;
}

export interface ReadingValidation {
  is_valid: boolean;
  warnings: string[];
  previous_reading?: number;
  expected_range?: {
    min: number;
    max: number;
  };
}

export interface MeterDueReading {
  meter: Meter;
  last_reading_date: string | null;
  days_since_reading: number;
}

// ============ HELPER FUNCTIONS ============

function extract_array_from_response<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  if (data && typeof data === 'object') {
    const values = Object.values(data);
    if (values.length > 0 && values.every(v => typeof v === 'string')) {
      console.warn('Received API navigation response instead of data');
      return [];
    }
  }
  console.warn('Unexpected response format, returning empty array');
  return [];
}

// ============ METER CRUD OPERATIONS ============

// GET /api/v1/meters/meters/
export const fetch_meters = async (filters?: MeterFilters): Promise<Meter[]> => {
  try {
    const response = await api_service.get('/meters/meters/', { params: filters });
    return extract_array_from_response<Meter>(response.results ?? response);
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

// GET /api/v1/meters/meters/?page=X
export const get_meters = async (
  page: number = 1,
  page_size: number = 20,
  filters?: MeterFilters
): Promise<PaginatedResponse<Meter>> => {
  try {
    const response = await api_service.get('/meters/meters/', {
      params: { page, page_size, ...filters }
    });
    const data = response.data ?? response;
    if (data && 'results' in data) return data;
    if (Array.isArray(data)) return { count: data.length, next: null, previous: null, results: data };
    return { count: 0, next: null, previous: null, results: [] };
  } catch (error) {
    console.error('Error fetching meters:', error);
    throw error;
  }
};

// GET /api/v1/meters/meters/{id}/
export const get_meter = async (meter_id: number): Promise<Meter> => {
  try {
    const response = await api_service.get(`/meters/meters/${meter_id}/`);
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching meter:', error);
    throw error;
  }
};

// POST /api/v1/meters/meters/
export const create_meter = async (data: MeterCreateData): Promise<Meter> => {
  try {
    const response = await api_service.post('/meters/meters/', data);
    return response.data ?? response;
  } catch (error) {
    console.error('Error creating meter:', error);
    throw error;
  }
};

// PATCH /api/v1/meters/meters/{id}/
export const update_meter = async (
  meter_id: number,
  data: MeterUpdateData
): Promise<Meter> => {
  try {
    const response = await api_service.patch(`/meters/meters/${meter_id}/`, data);
    return response.data ?? response;
  } catch (error) {
    console.error('Error updating meter:', error);
    throw error;
  }
};

// DELETE /api/v1/meters/meters/{id}/
export const delete_meter = async (meter_id: number): Promise<void> => {
  try {
    await api_service.delete(`/meters/meters/${meter_id}/`);
  } catch (error) {
    console.error('Error deleting meter:', error);
    throw error;
  }
};

// ============ METER READINGS ============

// GET /api/v1/meters/meters/{meter_id}/readings/
export const fetch_meter_readings = async (
  meter_id: string | number,
  limit?: number
): Promise<MeterReading[]> => {
  try {
    const response = await api_service.get(`/meters/meters/${meter_id}/readings/`, {
      params: limit ? { limit } : undefined
    });
    const data = response.data ?? response;
    return extract_array_from_response<MeterReading>(data);
  } catch (error) {
    console.error('Error fetching meter readings:', error);
    throw error;
  }
};

// GET /api/v1/meters/readings/?page=X
export const get_readings = async (
  page: number = 1,
  page_size: number = 20,
  filters?: ReadingFilters
): Promise<PaginatedResponse<MeterReading>> => {
  try {
    const response = await api_service.get('/meters/readings/', {
      params: { page, page_size, ...filters }
    });
    const data = response.data ?? response;
    if (data && 'results' in data) return data;
    if (Array.isArray(data)) return { count: data.length, next: null, previous: null, results: data };
    return { count: 0, next: null, previous: null, results: [] };
  } catch (error) {
    console.error('Error fetching readings:', error);
    throw error;
  }
};

// GET /api/v1/meters/readings/{id}/
export const get_reading = async (reading_id: number): Promise<MeterReading> => {
  try {
    const response = await api_service.get(`/meters/readings/${reading_id}/`);
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching reading:', error);
    throw error;
  }
};

// POST /api/v1/meters/readings/
export const create_meter_reading = async (
  data: CreateMeterReadingPayload | FormData
): Promise<MeterReading> => {
  try {
    const config = data instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};
    const response = await api_service.post('/meters/readings/', data, config);
    return response.data ?? response;
  } catch (error) {
    console.error('Error creating meter reading:', error);
    throw error;
  }
};

// POST /api/v1/meters/readings/ (legacy alias)
export const create_reading = async (data: ReadingCreateData): Promise<MeterReading> => {
  try {
    const response = await api_service.post('/meters/readings/', data);
    return response.data ?? response;
  } catch (error) {
    console.error('Error creating reading:', error);
    throw error;
  }
};

// POST /api/v1/meters/readings/ (with photo)
export const create_reading_with_photo = async (
  data: ReadingCreateData,
  photo_file: File,
  on_progress?: (progress: number) => void
): Promise<MeterReading> => {
  try {
    const form_data = new FormData();
    form_data.append('meter', data.meter.toString());
    form_data.append('current_reading', data.current_reading.toString());
    form_data.append('reading_date', data.reading_date);
    if (data.notes) form_data.append('notes', data.notes);
    form_data.append('photo', photo_file);

    const response = await api_service.post('/meters/readings/', form_data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progress_event) => {
        if (progress_event.total && on_progress) {
          on_progress(Math.round((progress_event.loaded * 100) / progress_event.total));
        }
      }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error creating reading with photo:', error);
    throw error;
  }
};

// PATCH /api/v1/meters/readings/{id}/
export const update_reading = async (
  reading_id: number,
  data: Partial<ReadingCreateData>
): Promise<MeterReading> => {
  try {
    const response = await api_service.patch(`/meters/readings/${reading_id}/`, data);
    return response.data ?? response;
  } catch (error) {
    console.error('Error updating reading:', error);
    throw error;
  }
};

// DELETE /api/v1/meters/readings/{id}/
export const delete_reading = async (reading_id: number): Promise<void> => {
  try {
    await api_service.delete(`/meters/readings/${reading_id}/`);
  } catch (error) {
    console.error('Error deleting reading:', error);
    throw error;
  }
};

// ============ METER STATISTICS ============

// GET /api/v1/meters/meters/{id}/stats/
export const fetch_meter_reading_stats = async (
  meter_id: string | number,
  date_from?: string,
  date_to?: string
): Promise<MeterReadingStats> => {
  try {
    const response = await api_service.get(`/meters/meters/${meter_id}/stats/`, {
      params: {
        ...(date_from && { date_from }),
        ...(date_to && { date_to })
      }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching meter reading stats:', error);
    throw error;
  }
};

export const get_meter_stats = async (
  meter_id: number,
  date_from?: string,
  date_to?: string
): Promise<MeterReadingStats> => {
  return fetch_meter_reading_stats(meter_id, date_from, date_to);
};

// GET /api/v1/meters/meters/{id}/readings/ (history alias)
export const get_meter_history = async (
  meter_id: number,
  limit?: number
): Promise<MeterReading[]> => {
  return fetch_meter_readings(meter_id, limit);
};

// GET /api/v1/meters/meters/{id}/latest_reading/
export const fetch_latest_reading = async (meter_id: string | number): Promise<MeterReading | null> => {
  try {
    const response = await api_service.get(`/meters/meters/${meter_id}/latest_reading/`);
    return response.data ?? response ?? null;
  } catch (error) {
    console.error('Error fetching latest reading:', error);
    throw error;
  }
};

export const get_latest_reading = async (meter_id: number): Promise<MeterReading | null> => {
  return fetch_latest_reading(meter_id);
};

// GET /api/v1/meters/meters/summary/
export const fetch_meter_summary = async (): Promise<MeterStatistics> => {
  try {
    const response = await api_service.get('/meters/meters/summary/');
    return response.data ?? response;
  } catch (error) {
    console.error('Error fetching meter summary:', error);
    throw error;
  }
};

export const get_meters_summary = async (): Promise<MeterStatistics> => {
  return fetch_meter_summary();
};

// ============ SPECIALIZED QUERIES ============

// GET /api/v1/meters/readings/unbilled/
export const get_unbilled_readings = async (): Promise<MeterReading[]> => {
  try {
    const response = await api_service.get('/meters/readings/unbilled/');
    return extract_array_from_response<MeterReading>(response.data ?? response);
  } catch (error) {
    console.error('Error fetching unbilled readings:', error);
    throw error;
  }
};

// GET /api/v1/meters/readings/unbilled-for-invoicing/
export const fetch_unbilled_readings_for_invoicing = async (): Promise<UnbilledReadingForInvoicing[]> => {
  try {
    const response = await api_service.get('/meters/readings/unbilled-for-invoicing/');
    const data = response.data ?? response;
    if (data?.results && Array.isArray(data.results)) return data.results;
    if (Array.isArray(data)) return data;
    console.warn('Unexpected response format for unbilled readings:', response);
    return [];
  } catch (error) {
    console.error('Error fetching unbilled readings for invoicing:', error);
    return [];
  }
};

// GET /api/v1/meters/readings/due/
export const get_due_readings = async (): Promise<MeterDueReading[]> => {
  try {
    const response = await api_service.get('/meters/readings/due/');
    return extract_array_from_response<MeterDueReading>(response.data ?? response);
  } catch (error) {
    console.error('Error fetching due readings:', error);
    throw error;
  }
};

// ============ VALIDATION ============

// GET /api/v1/meters/meters/validate_meter_number/?meter_number=X
export const validate_meter_number = async (
  meter_number: string,
  exclude_meter_id?: number
): Promise<{ is_valid: boolean; message?: string }> => {
  try {
    const response = await api_service.get('/meters/meters/validate_meter_number/', {
      params: { meter_number, ...(exclude_meter_id && { exclude: exclude_meter_id }) }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error validating meter number:', error);
    throw error;
  }
};

// POST /api/v1/meters/readings/validate/
export const validate_reading = async (
  meter_id: number,
  current_reading: number
): Promise<ReadingValidation> => {
  try {
    const response = await api_service.post('/meters/readings/validate/', {
      meter_id,
      current_reading
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error validating reading:', error);
    throw error;
  }
};

// ============ BULK OPERATIONS ============

// POST /api/v1/meters/readings/bulk_create/
export const bulk_create_readings = async (
  readings: ReadingCreateData[]
): Promise<{ created: number; failed: number; errors: any[] }> => {
  try {
    const response = await api_service.post('/meters/readings/bulk_create/', { readings });
    return response.data ?? response;
  } catch (error) {
    console.error('Error bulk creating readings:', error);
    throw error;
  }
};

// ============ IMPORT/EXPORT ============

// GET /api/v1/meters/meters/export_csv/
export const export_meters_csv = async (filters?: MeterFilters): Promise<Blob> => {
  try {
    const response = await api_service.get('/meters/meters/export_csv/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error exporting meters to CSV:', error);
    throw error;
  }
};

// GET /api/v1/meters/meters/export_excel/
export const export_meters_excel = async (filters?: MeterFilters): Promise<Blob> => {
  try {
    const response = await api_service.get('/meters/meters/export_excel/', {
      params: filters,
      responseType: 'blob'
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error exporting meters to Excel:', error);
    throw error;
  }
};

// GET /api/v1/meters/readings/export_csv/ or export_excel/
export const export_readings = async (
  format: 'csv' | 'excel',
  filters?: ReadingFilters
): Promise<Blob> => {
  try {
    const response = await api_service.get(`/meters/readings/export_${format}/`, {
      params: filters,
      responseType: 'blob'
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error exporting readings:', error);
    throw error;
  }
};

// POST /api/v1/meters/meters/import_meters/
export const import_meters = async (
  file: File
): Promise<{ imported: number; failed: number; errors: any[] }> => {
  try {
    const form_data = new FormData();
    form_data.append('file', file);
    const response = await api_service.post('/meters/meters/import_meters/', form_data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error importing meters:', error);
    throw error;
  }
};

// POST /api/v1/meters/readings/import_readings/
export const import_readings = async (
  file: File
): Promise<{ imported: number; failed: number; errors: any[] }> => {
  try {
    const form_data = new FormData();
    form_data.append('file', file);
    const response = await api_service.post('/meters/readings/import_readings/', form_data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data ?? response;
  } catch (error) {
    console.error('Error importing readings:', error);
    throw error;
  }
};

// ============ CLASS-BASED SERVICE (for backward compatibility) ============

class MeterService {
  get_meters = get_meters;
  get_meter = get_meter;
  create_meter = create_meter;
  update_meter = update_meter;
  delete_meter = delete_meter;
  get_meter_history = get_meter_history;
  get_meter_stats = get_meter_stats;
  validate_meter_number = validate_meter_number;
  get_readings = get_readings;
  get_reading = get_reading;
  create_reading = create_reading;
  create_reading_with_photo = create_reading_with_photo;
  update_reading = update_reading;
  delete_reading = delete_reading;
  get_unbilled_readings = get_unbilled_readings;
  bulk_create_readings = bulk_create_readings;
  get_latest_reading = get_latest_reading;
  validate_reading = validate_reading;
  get_due_readings = get_due_readings;
  export_readings = export_readings;
  get_meters_summary = get_meters_summary;
  fetch_unbilled_readings_for_invoicing = fetch_unbilled_readings_for_invoicing;
}

export const meter_service = new MeterService();
export default meter_service;